import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { usePrimeArtifact } from '../../store/hooks/usePrimeArtifact';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import styles from './ArtifactTransitionTracer.module.css';

interface ArtifactTransitionTracerProps {
  artifactId: string;
  artifactName: string;
}

type NodeType = 'state' | 'process';

interface GraphNode {
  id: string;
  type: NodeType;
  label: string;
  processId?: string;
}

interface GraphEdge {
  source: string;
  target: string;
  label: string;
  transitionId: string;
}

interface Graph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

const ArtifactTransitionTracer: React.FC<ArtifactTransitionTracerProps> = ({
  artifactId,
  artifactName,
}) => {
  const { fetchStates, fetchTransitions, fetchProcesses } = usePrimeArtifact(artifactId);

  // Create memoized selectors
  const selectArtifactData = useMemo(
    () =>
      createSelector(
        [
          (state: RootState) => state.primeArtifacts.primeArtifactStates,
          (state: RootState) => state.primeArtifacts.primeArtifactTransitions,
          (state: RootState) => state.primeArtifacts.primeProcesses,
        ],
        (states, transitions, processes) => ({
          states,
          transitions,
          processes,
        })
      ),
    []
  );

  const { states, transitions, processes } = useSelector(selectArtifactData);

  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [filterTransitionType, setFilterTransitionType] = useState<string | null>(null);
  const [graph, setGraph] = useState<Graph | null>(null);
  const [highlightedNodes, setHighlightedNodes] = useState<Set<string>>(new Set());
  const [highlightedEdges, setHighlightedEdges] = useState<Set<string>>(new Set());

  const buildGraph = useCallback(() => {
    console.log('[ArtifactTransitionTracer] Starting graph build');
    const newNodes: GraphNode[] = [];
    const newEdges: GraphEdge[] = [];

    // Create a map to track state positions
    const statePositions = new Map<string, number>();
    let currentPosition = 0;

    // First pass: Add state nodes and assign positions
    states.forEach((state) => {
      console.log('[ArtifactTransitionTracer] Adding state node:', state.name);
      statePositions.set(state.id, currentPosition++);
      newNodes.push({
        id: `state-${state.id}`,
        type: 'state',
        label: state.name,
      });
    });

    // Second pass: Add process nodes and edges
    transitions.forEach((transition) => {
      // Apply filter if set
      if (filterTransitionType && transition.default_transition_type !== filterTransitionType) {
        console.log('[ArtifactTransitionTracer] Skipping filtered transition:', transition.id);
        return;
      }

      const fromState = states.find((s) => s.id === transition.from_state_ref);
      const toState = states.find((s) => s.id === transition.to_state_ref);
      const process = processes.find((p) => p.id === transition.prime_process_ref);

      if (fromState && toState) {
        console.log('[ArtifactTransitionTracer] Adding transition:', {
          id: transition.id,
          from: fromState.name,
          to: toState.name,
          process: process?.name,
        });

        // Add process node if it exists
        if (process) {
          const processNodeId = `process-${process.id}`;

          // Check if process node already exists
          if (!newNodes.some((node) => node.id === processNodeId)) {
            newNodes.push({
              id: processNodeId,
              type: 'process',
              label: process.name,
              processId: process.id,
            });
          }

          // Add edges with proper ordering
          newEdges.push({
            source: `state-${fromState.id}`,
            target: processNodeId,
            label: '',
            transitionId: transition.id,
          });

          newEdges.push({
            source: processNodeId,
            target: `state-${toState.id}`,
            label: '',
            transitionId: transition.id,
          });
        } else {
          // If no process, connect states directly
          newEdges.push({
            source: `state-${fromState.id}`,
            target: `state-${toState.id}`,
            label: transition.name || `${fromState.name} → ${toState.name}`,
            transitionId: transition.id,
          });
        }
      } else {
        console.warn('[ArtifactTransitionTracer] Missing state reference for transition:', {
          transitionId: transition.id,
          fromStateId: transition.from_state_ref,
          toStateId: transition.to_state_ref,
          fromStateFound: !!fromState,
          toStateFound: !!toState,
        });
      }
    });

    // Sort nodes based on their type and position
    newNodes.sort((a, b) => {
      if (a.type === b.type) {
        return 0;
      }
      return a.type === 'state' ? -1 : 1;
    });

    console.log('[ArtifactTransitionTracer] Graph built:', {
      nodeCount: newNodes.length,
      edgeCount: newEdges.length,
    });

    setGraph({ nodes: newNodes, edges: newEdges });
  }, [states, transitions, processes, filterTransitionType]);

  useEffect(() => {
    if (artifactId) {
      console.log('[ArtifactTransitionTracer] Fetching data for artifact:', artifactId);
      void fetchStates();
      void fetchTransitions();
      void fetchProcesses();
    }
  }, [artifactId, fetchStates, fetchTransitions, fetchProcesses]);

  useEffect(() => {
    console.log('[ArtifactTransitionTracer] Data updated:', {
      statesCount: states.length,
      transitionsCount: transitions.length,
      processesCount: processes.length,
      hasFilter: !!filterTransitionType,
    });

    if (states.length > 0 && transitions.length > 0) {
      console.log('[ArtifactTransitionTracer] Building graph with data:', {
        states: states.map((s) => ({ id: s.id, name: s.name })),
        transitions: transitions.map((t) => ({
          id: t.id,
          from: t.from_state_ref,
          to: t.to_state_ref,
          type: t.default_transition_type,
        })),
      });
      buildGraph();
    }
  }, [states, transitions, processes, filterTransitionType, buildGraph]);

  const handleNodeClick = (node: GraphNode) => {
    setSelectedNode(node);
    // Update highlighted path based on the selected node
    const newHighlightedNodes = new Set<string>();
    const newHighlightedEdges = new Set<string>();

    // Add the selected node to highlighted nodes
    newHighlightedNodes.add(node.id);

    // If it's a process node, highlight connected states
    if (node.type === 'process') {
      graph?.edges
        .filter((edge) => edge.source === node.id || edge.target === node.id)
        .forEach((edge) => {
          newHighlightedEdges.add(`${edge.source}-${edge.target}`);
          newHighlightedNodes.add(edge.source);
          newHighlightedNodes.add(edge.target);
        });
    }

    setHighlightedNodes(newHighlightedNodes);
    setHighlightedEdges(newHighlightedEdges);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setFilterTransitionType(value === 'all' ? null : value);
  };

  const clearSelection = () => {
    setSelectedNode(null);
    setHighlightedNodes(new Set());
    setHighlightedEdges(new Set());
  };

  const renderGraph = () => {
    if (!graph) return null;

    // Group nodes by their level (distance from start)
    const nodeLevels = new Map<string, number>();
    const visited = new Set<string>();

    // Find all start nodes (nodes with no incoming edges)
    const startNodes = graph.nodes.filter((node) => {
      return !graph.edges.some((edge) => edge.target === node.id);
    });

    console.log(
      '[ArtifactTransitionTracer] Start nodes:',
      startNodes.map((n) => n.label)
    );

    // BFS to assign levels to nodes
    const queue = [...startNodes.map((node) => ({ node, level: 0 }))];
    while (queue.length > 0) {
      const { node, level } = queue.shift()!;
      if (visited.has(node.id)) continue;
      visited.add(node.id);
      nodeLevels.set(node.id, level);

      // Add connected nodes to queue
      graph.edges
        .filter((edge) => edge.source === node.id)
        .forEach((edge) => {
          const targetNode = graph.nodes.find((n) => n.id === edge.target);
          if (targetNode && !visited.has(targetNode.id)) {
            queue.push({ node: targetNode, level: level + 1 });
          }
        });
    }

    // Group nodes by level
    const nodesByLevel = new Map<number, GraphNode[]>();
    graph.nodes.forEach((node) => {
      const level = nodeLevels.get(node.id) || 0;
      if (!nodesByLevel.has(level)) {
        nodesByLevel.set(level, []);
      }
      nodesByLevel.get(level)!.push(node);
    });

    // Sort levels
    const sortedLevels = Array.from(nodesByLevel.keys()).sort((a, b) => a - b);

    console.log('[ArtifactTransitionTracer] Graph layout:', {
      levels: sortedLevels.map((level) => ({
        level,
        nodeCount: nodesByLevel.get(level)?.length,
      })),
    });

    return (
      <div className={styles.artifactTransitionGraph} data-testid="artifact-transition-graph">
        <div className={styles.graphNodes}>
          {sortedLevels.map((level) => {
            const levelNodes = nodesByLevel.get(level)!;
            const stateNodes = levelNodes.filter((n) => n.type === 'state');
            const processNodes = levelNodes.filter((n) => n.type === 'process');

            return (
              <div key={level} className={styles.graphNodeRow}>
                {/* Render state nodes */}
                {stateNodes.map((node) => (
                  <div
                    key={node.id}
                    className={`${styles.graphNode} ${styles[node.type]} ${
                      highlightedNodes.has(node.id) ? styles.highlighted : ''
                    } ${selectedNode?.id === node.id ? styles.selected : ''}`}
                    data-testid={
                      node.type === 'process' ? `process-node-${node.id}` : `state-node-${node.id}`
                    }
                    onClick={() => handleNodeClick(node)}
                  >
                    {node.label}
                  </div>
                ))}

                {/* Render process nodes */}
                {processNodes.map((node) => (
                  <div
                    key={node.id}
                    className={`${styles.graphNode} ${styles[node.type]} ${
                      highlightedNodes.has(node.id) ? styles.highlighted : ''
                    } ${selectedNode?.id === node.id ? styles.selected : ''}`}
                    data-testid={
                      node.type === 'process' ? `process-node-${node.id}` : `state-node-${node.id}`
                    }
                    onClick={() => handleNodeClick(node)}
                    data-label={node.label}
                  />
                ))}
              </div>
            );
          })}
        </div>

        <div className={styles.graphEdges}>
          {graph.edges.map((edge) => {
            const sourceNode = graph.nodes.find((n) => n.id === edge.source);
            const targetNode = graph.nodes.find((n) => n.id === edge.target);
            if (!sourceNode || !targetNode) return null;

            const sourceLevel = nodeLevels.get(sourceNode.id) || 0;
            const targetLevel = nodeLevels.get(targetNode.id) || 0;

            // Calculate positions for the edge
            const sourceLevelNodes = nodesByLevel.get(sourceLevel) || [];
            const targetLevelNodes = nodesByLevel.get(targetLevel) || [];

            if (sourceLevelNodes.length === 0 || targetLevelNodes.length === 0) return null;

            const sourceIndex = sourceLevelNodes.findIndex((n) => n.id === sourceNode.id);
            const targetIndex = targetLevelNodes.findIndex((n) => n.id === targetNode.id);

            if (sourceIndex === -1 || targetIndex === -1) return null;

            // Calculate the position of the source and target nodes
            const sourceX = (sourceIndex + 0.5) * (100 / sourceLevelNodes.length);
            const targetX = (targetIndex + 0.5) * (100 / targetLevelNodes.length);

            // Calculate the vertical position based on the level
            const sourceY = sourceLevel * 150 + 50; // Increased spacing between levels
            const targetY = targetLevel * 150 + 50;

            // Calculate the angle and length for the diagonal line
            const dx = targetX - sourceX;
            const dy = targetY - sourceY;
            const angle = Math.atan2(dy, dx) * (180 / Math.PI);
            const length = Math.sqrt(dx * dx + dy * dy);

            // Calculate the starting position of the line
            const startX = sourceX;
            const startY = sourceY;

            return (
              <div
                key={`${edge.source}-${edge.target}`}
                className={`${styles.graphEdge} ${
                  highlightedEdges.has(`${edge.source}-${edge.target}`) ? styles.highlighted : ''
                }`}
                style={{
                  left: `${startX}%`,
                  top: `${startY}px`,
                  width: `${length}%`,
                  transform: `rotate(${angle}deg)`,
                }}
              >
                {edge.label && <div className={styles.edgeLabel}>{edge.label}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderNodeDetails = () => {
    if (!selectedNode) return null;

    return (
      <div className={styles.nodeDetails} data-testid="node-details">
        <h3>{selectedNode.label}</h3>
        <div className={styles.detailsSection}>
          <h4>Type</h4>
          <p>{selectedNode.type}</p>
        </div>
        {selectedNode.type === 'process' && (
          <div className={styles.detailsSection}>
            <h4>Connected States</h4>
            <div>
              {graph?.edges
                .filter(
                  (edge) => edge.source === selectedNode.id || edge.target === selectedNode.id
                )
                .map((edge) => {
                  const connectedNode = graph.nodes.find(
                    (n) => n.id === (edge.source === selectedNode.id ? edge.target : edge.source)
                  );
                  return (
                    <div key={edge.source + edge.target}>
                      {connectedNode?.label || 'Unknown State'}
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.artifactTransitionTracer}>
      <div className={styles.tracerHeader}>
        <h2>Artifact Transition Tracer: {artifactName}</h2>
        <div className={styles.tracerControls}>
          <select
            data-testid="transition-filter"
            value={filterTransitionType || 'all'}
            onChange={handleFilterChange}
          >
            <option value="all">All Transition Types</option>
            <option value="PROCESSITEM">Process Item</option>
            <option value="MATERIAL">Material</option>
            <option value="RESOURCE">Resource</option>
            <option value="PROCESSITEM_PERSISTENT">Process Item Persistent</option>
            <option value="CREATION">Creation</option>
          </select>
          <button onClick={clearSelection}>Clear Selection</button>
        </div>
      </div>

      <div className={styles.tracerContent}>
        <div className={styles.tracerGraph}>{renderGraph()}</div>
        <div className={styles.tracerDetails}>{renderNodeDetails()}</div>
      </div>
    </div>
  );
};

export default ArtifactTransitionTracer;
